import React, { useState, useEffect } from 'react';
import { tasksAPI, achievementsAPI, fileAPI, usersAPI } from '../services/api';
import { Task, UserTask, Achievement, User } from '../types';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import FileViewer from '../components/FileViewer';
import PageHeader from '../components/PageHeader';
import { extractTaskFileNames } from '../utils/fileUtils';
import { useFileViewer } from '../hooks/useFileViewer';
import TaskFileList from '../components/TaskFileList';
import '../styles/global.css';

const Tasks: React.FC = () => {
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [myTasks, setMyTasks] = useState<UserTask[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [colleagues, setColleagues] = useState<User[]>([]);
  const [witnessPending, setWitnessPending] = useState<UserTask[]>([]);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [witnessUserId, setWitnessUserId] = useState<number | null>(null);
  const [witnessSearch, setWitnessSearch] = useState('');

  const [witnessRespondTask, setWitnessRespondTask] = useState<UserTask | null>(null);
  const [witnessComment, setWitnessComment] = useState('');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [taskMessage, setTaskMessage] = useState('');
  const [selectedUserTask, setSelectedUserTask] = useState<UserTask | null>(null);
  const fileViewer = useFileViewer();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [available, allTasksData, myTasksData, achData, colleaguesData, witnessData] = await Promise.all([
        tasksAPI.getAvailableTasks(),
        tasksAPI.getTasks(),
        tasksAPI.getMyTasks(),
        achievementsAPI.getAchievements(),
        usersAPI.getColleagues(),
        tasksAPI.getWitnessPendingTasks(),
      ]);
      setAvailableTasks(available);
      setAllTasks(allTasksData);
      setMyTasks(myTasksData);
      setAchievements(achData);
      setColleagues(colleaguesData);
      setWitnessPending(witnessData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTask = async () => {
    if (!selectedTask) return;
    try {
      setSubmitting(true);
      setError('');
      setMessage('');

      const filePaths: string[] = [];
      for (const file of selectedFiles) {
        const up = await tasksAPI.uploadTaskFile(selectedTask.id, file);
        filePaths.push(up.file_path);
      }

      await tasksAPI.submitTask(selectedTask.id, {
        submission_text: submissionText,
        file_paths: filePaths.length > 0 ? filePaths : undefined,
        witness_user_id: witnessUserId ?? undefined,
      });

      setMessage(witnessUserId
        ? 'Задание отправлено! Коллега получит уведомление для подтверждения участия.'
        : 'Задание успешно отправлено на проверку!');
      setSelectedTask(null);
      setSubmissionText('');
      setSelectedFiles([]);
      setWitnessUserId(null);
      setWitnessSearch('');
      setTimeout(() => loadData(), 800);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка отправки задания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWitnessRespond = async (confirmed: boolean) => {
    if (!witnessRespondTask) return;
    try {
      await tasksAPI.witnessRespond(witnessRespondTask.id, confirmed, witnessComment || undefined);
      setWitnessRespondTask(null);
      setWitnessComment('');
      setMessage(confirmed ? 'Участие подтверждено!' : 'Участие отклонено.');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка ответа');
    }
  };

  const getAchievementTitle = (achievementId: number) => {
    const a = achievements.find(a => a.id === achievementId);
    return a ? a.title : 'Неизвестное достижение';
  };

  const getTaskInfo = (taskId: number): Task => {
    return availableTasks.find(t => t.id === taskId)
      || allTasks.find(t => t.id === taskId)
      || { id: taskId, title: 'Задание #' + taskId, description: 'Нет описания', achievement_id: 0, percentage_value: 0, created_at: '' };
  };

  const getColleagueName = (id: number) => {
    const c = colleagues.find(c => c.id === id);
    return c ? c.full_name || c.username : `Сотрудник #${id}`;
  };

  const filteredColleagues = colleagues.filter(c =>
    (c.full_name || c.username).toLowerCase().includes(witnessSearch.toLowerCase())
  );

  const witnessTaskInfo = (ut: UserTask) => {
    // Find task info from all sources
    const task = availableTasks.find(t => t.id === ut.task_id)
      || myTasks.map(m => availableTasks.find(t => t.id === m.task_id)).find(Boolean);
    return task;
  };

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '2rem' }}>
      <PageHeader title="Задания" />

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}
      {taskMessage && <div className="error" style={{ marginBottom: '1rem' }}>{taskMessage}</div>}

      {/* ── Входящие запросы на подтверждение участия ── */}
      {witnessPending.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: 'var(--primary-orange)', marginBottom: '1rem' }}>
            🔔 Запросы на подтверждение участия ({witnessPending.length})
          </h2>
          {witnessPending.map(ut => {
            const allTasks = availableTasks;
            const task = allTasks.find(t => t.id === ut.task_id);
            return (
              <div key={ut.id} className="card mb-3" style={{ border: '2px solid var(--primary-orange)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: 'var(--primary-orange)', marginBottom: '0.5rem' }}>
                      Вас указали участником в задании
                    </h3>
                    <p style={{ marginBottom: '0.25rem' }}>
                      <strong>Задание:</strong> {task?.title || `#${ut.task_id}`}
                    </p>
                    {task && (
                      <p style={{ marginBottom: '0.25rem' }}>
                        <strong>Достижение:</strong> {getAchievementTitle(task.achievement_id)}
                      </p>
                    )}
                    {ut.submission_text && (
                      <p style={{ marginBottom: '0.25rem', fontSize: '14px', color: 'var(--text-muted)' }}>
                        <strong>Описание исполнителя:</strong> {ut.submission_text}
                      </p>
                    )}
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                      Отправлено: {ut.submitted_at ? new Date(ut.submitted_at).toLocaleString('ru') : '—'}
                    </p>
                  </div>
                  <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setWitnessRespondTask(ut)}
                    >
                      Ответить
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-2">
        {/* ── Доступные задания ── */}
        <div>
          <h2 style={{ color: 'var(--primary-orange)', marginBottom: '1.5rem' }}>Доступные задания</h2>
          {availableTasks.length === 0 ? (
            <div className="card text-center p-4">
              <p style={{ color: 'var(--text-muted)' }}>Нет доступных заданий</p>
              <p style={{ color: 'var(--text-muted)' }}>Все задания выполнены или ожидают проверки</p>
            </div>
          ) : (
            availableTasks.map(task => (
              <div key={task.id} className="card mb-3">
                <h3 style={{ color: 'var(--primary-orange)', marginBottom: '0.75rem' }}>
                  Задание «{task.title}»
                </h3>
                <p className="mb-3">{task.description}</p>
                <div style={{ marginBottom: '10px' }}>
                  <StatusBadge status="pending" label={getAchievementTitle(task.achievement_id)} />
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>+{task.percentage_value}%</span>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const existing = myTasks.find(ut => ut.task_id === task.id);
                      if (existing) {
                        if (existing.status === 'submitted' || existing.status === 'waiting_witness') {
                          setTaskMessage('Задание уже отправлено на проверку или ожидает подтверждения коллеги!');
                        } else if (existing.status === 'approved') {
                          setTaskMessage('Это задание уже одобрено!');
                        } else {
                          setSelectedTask(task);
                        }
                        setTimeout(() => setTaskMessage(''), 3000);
                        return;
                      }
                      setSelectedTask(task);
                    }}
                  >
                    Выполнить
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Мои задания ── */}
        <div>
          <h2 style={{ color: 'var(--primary-orange)', marginBottom: '1.5rem' }}>Мои задания</h2>
          {myTasks.length === 0 ? (
            <div className="card text-center p-4">
              <p style={{ color: 'var(--text-muted)' }}>У вас пока нет заданий</p>
            </div>
          ) : (
            myTasks.map(userTask => {
              const taskInfo = getTaskInfo(userTask.task_id);
              return (
                <div key={userTask.id} className="card mb-3">
                  <h3 className="mb-2" style={{ color: 'var(--primary-orange)', fontSize: '16px', fontWeight: '600' }}>
                    Задание «{taskInfo.title}»
                  </h3>
                  <div className="d-flex gap-2" style={{ marginBottom: '10px', flexWrap: 'wrap' }}>
                    <StatusBadge status="pending" label={getAchievementTitle(taskInfo.achievement_id)} />
                    <StatusBadge status="submitted" label={`+${taskInfo.percentage_value}%`} />
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <StatusBadge status={userTask.status} />
                  </div>

                  {/* Witness status badge */}
                  {userTask.witness_user_id && (
                    <div style={{ marginBottom: '0.5rem', fontSize: '13px', color: 'var(--text-muted)' }}>
                      👥 Коллега: <strong>{getColleagueName(userTask.witness_user_id)}</strong>
                      {' — '}
                      {userTask.witness_status === 'pending' && <span style={{ color: '#f59e0b' }}>ожидает ответа</span>}
                      {userTask.witness_status === 'confirmed' && <span style={{ color: '#10b981' }}>✓ подтвердил участие</span>}
                      {userTask.witness_status === 'rejected' && <span style={{ color: '#ef4444' }}>✗ отклонил участие</span>}
                      {userTask.witness_comment && (
                        <span style={{ display: 'block', marginTop: '2px', fontStyle: 'italic' }}>
                          «{userTask.witness_comment}»
                        </span>
                      )}
                    </div>
                  )}

                  {userTask.submitted_at && (
                    <p className="mt-2" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Отправлено: {new Date(userTask.submitted_at).toLocaleDateString('ru')}
                    </p>
                  )}
                  {userTask.review_comment && (
                    <div className="mt-2 p-2" style={{ backgroundColor: 'var(--bg-surface)', fontSize: '14px' }}>
                      <strong>Комментарий:</strong> {userTask.review_comment}
                    </div>
                  )}
                  <div className="mt-2 d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setSelectedUserTask(userTask)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Просмотр
                    </button>
                    {/* Re-submit button if witness rejected */}
                    {userTask.status === 'witness_rejected' && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          const task = availableTasks.find(t => t.id === userTask.task_id);
                          if (task) setSelectedTask(task);
                        }}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        Переотправить
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Модалка отправки задания ── */}
      {selectedTask && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '560px', width: '90%', maxHeight: '88vh', overflowY: 'auto' }}>
            <h2 style={{ color: 'var(--primary-orange)', marginBottom: '1.5rem' }}>Выполнить задание</h2>
            <h3>«{selectedTask.title}»</h3>
            <p className="mb-2">{selectedTask.description}</p>
            <div style={{ marginBottom: '15px' }}>
              <span style={{
                background: 'var(--bg-surface)', color: 'var(--text-secondary)',
                padding: '6px 12px', borderRadius: '4px', fontSize: '13px',
                border: '1px solid var(--border-light)', display: 'inline-block', marginRight: '10px'
              }}>
                {getAchievementTitle(selectedTask.achievement_id)}
              </span>
              <span style={{
                background: 'var(--orange-color)', color: 'white',
                padding: '6px 12px', borderRadius: '4px', fontSize: '13px', display: 'inline-block'
              }}>
                +{selectedTask.percentage_value}%
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Описание выполнения:</label>
              <textarea
                className="form-control" rows={4} value={submissionText}
                onChange={e => setSubmissionText(e.target.value)}
                placeholder="Опишите, как вы выполнили это задание..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Прикрепить файлы (необязательно):</label>
              <input type="file" multiple className="form-control"
                onChange={e => setSelectedFiles(Array.from(e.target.files || []))} />
              {selectedFiles.length > 0 && (
                <div className="mt-1" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  Выбрано: {selectedFiles.length} файл(а)
                </div>
              )}
            </div>

            {/* ── Witness picker ── */}
            <div className="form-group">
              <label className="form-label">
                👥 Коллега-участник проекта (необязательно):
              </label>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', marginTop: 0 }}>
                Если над заданием работал коллега — укажите его. Он получит уведомление и должен подтвердить участие, после чего задание уйдёт на проверку.
              </p>

              {witnessUserId ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 14px', borderRadius: '8px',
                  background: 'var(--bg-surface)', border: '1px solid var(--primary-orange)'
                }}>
                  <span style={{ flex: 1 }}>
                    ✓ <strong>{getColleagueName(witnessUserId)}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => { setWitnessUserId(null); setWitnessSearch(''); }}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    Убрать
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text" className="form-control" placeholder="Поиск по имени..."
                    value={witnessSearch} onChange={e => setWitnessSearch(e.target.value)}
                    style={{ marginBottom: '6px' }}
                  />
                  {witnessSearch && (
                    <div style={{
                      maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border-light)',
                      borderRadius: '6px', background: 'var(--bg-surface)'
                    }}>
                      {filteredColleagues.length === 0 ? (
                        <div style={{ padding: '10px', color: 'var(--text-muted)', fontSize: '13px' }}>
                          Ничего не найдено
                        </div>
                      ) : filteredColleagues.map(c => (
                        <div
                          key={c.id}
                          onClick={() => { setWitnessUserId(c.id); setWitnessSearch(''); }}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', fontSize: '14px',
                            borderBottom: '1px solid var(--border-light)',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          {c.full_name || c.username}
                          {c.position_id && (
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                              ID {c.id}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedTask(null); setSubmissionText('');
                  setSelectedFiles([]); setWitnessUserId(null); setWitnessSearch('');
                }}
                disabled={submitting}
              >
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitTask}
                disabled={submitting || !submissionText.trim()}
              >
                {submitting ? 'Отправка...' : witnessUserId ? 'Отправить (с подтверждением коллеги)' : 'Отправить на проверку'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Модалка ответа свидетеля ── */}
      {witnessRespondTask && (() => {
        const ut = witnessRespondTask;
        const taskInfo = getTaskInfo(ut.task_id);
        const fileNames = extractTaskFileNames(ut);

        return (
          <Modal isOpen={true} onClose={() => { setWitnessRespondTask(null); setWitnessComment(''); }} maxWidth="900px">
            <h2 className="modal-title">Задание: {taskInfo.title}</h2>
            <p className="modal-subtitle">ID: #{ut.task_id}</p>

            {/* Мета-блок */}
            <div className="section-card" style={{ marginBottom: '1.25rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>Описание: </span>
                {taskInfo.description}
              </div>
              <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Достижение</div>
                  <strong>{getAchievementTitle(taskInfo.achievement_id)}</strong>
                </div>
                <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Исполнитель</div>
                  <strong>{getColleagueName(ut.user_id)}</strong>
                </div>
                <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Ценность</div>
                  <strong style={{ color: 'var(--primary-orange)', fontSize: '1.25rem' }}>
                    +{taskInfo.percentage_value}%
                  </strong>
                </div>
              </div>
            </div>

            {/* Текст выполнения */}
            {ut.submission_text && (
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>Описание выполнения</h3>
                <div className="notification-modal-message">
                  <p>{ut.submission_text}</p>
                </div>
              </div>
            )}

            {/* Файлы */}
            {fileNames.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>
                  Прикреплённые файлы ({fileNames.length})
                </h3>
                <TaskFileList
                  filenames={fileNames}
                  folder="tasks"
                  onOpen={(i) => fileViewer.open(fileNames, i)}
                  onDownload={(fn) => fileAPI.downloadFile(fn)}
                />
              </div>
            )}

            {/* Комментарий и кнопки */}
            <div className="form-group">
              <label className="form-label">Комментарий (необязательно):</label>
              <textarea className="form-control" rows={3}
                value={witnessComment} onChange={e => setWitnessComment(e.target.value)}
                placeholder="Добавьте комментарий к своему решению..."
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={() => { setWitnessRespondTask(null); setWitnessComment(''); }}>
                Позже
              </button>
              <button className="btn btn-danger" onClick={() => handleWitnessRespond(false)}>
                Отклонить
              </button>
              <button className="btn btn-success" onClick={() => handleWitnessRespond(true)}>
                Подтвердить участие
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Просмотр задания ── */}
      <Modal isOpen={!!selectedUserTask} onClose={() => setSelectedUserTask(null)} maxWidth="860px">
        {selectedUserTask && (() => {
          const ut = selectedUserTask;
          const taskInfo = getTaskInfo(ut.task_id);
          const fileNames = extractTaskFileNames(ut);

          return (
            <>
              <h2 className="modal-title">{taskInfo.title}</h2>
              <p className="modal-subtitle">#{ut.task_id}</p>

              <div className="section-card" style={{ marginBottom: '1.25rem' }}>
                <p style={{ margin: 0 }}>{taskInfo.description}</p>
                <div className="d-flex gap-3 mt-3" style={{ flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Достижение</div>
                    <StatusBadge status="pending" label={getAchievementTitle(taskInfo.achievement_id)} />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Ценность</div>
                    <StatusBadge status="submitted" label={`+${taskInfo.percentage_value}%`} />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Статус</div>
                    <StatusBadge status={ut.status} />
                  </div>
                </div>
              </div>

              {/* Witness info */}
              {ut.witness_user_id && (
                <div className="section-card" style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                    👥 Коллега-участник
                  </div>
                  <strong>{getColleagueName(ut.witness_user_id)}</strong>
                  <span style={{ marginLeft: '12px' }}>
                    {ut.witness_status === 'pending' && <StatusBadge status="pending" label="Ожидает ответа" />}
                    {ut.witness_status === 'confirmed' && <StatusBadge status="approved" label="Подтвердил участие" />}
                    {ut.witness_status === 'rejected' && <StatusBadge status="rejected" label="Отклонил участие" />}
                  </span>
                  {ut.witness_comment && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', fontStyle: 'italic', color: 'var(--text-muted)' }}>
                      «{ut.witness_comment}»
                    </p>
                  )}
                  {ut.witness_responded_at && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                      {new Date(ut.witness_responded_at).toLocaleString('ru')}
                    </p>
                  )}
                </div>
              )}

              {ut.submission_text && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>Описание выполнения</h3>
                  <div className="notification-modal-message"><p>{ut.submission_text}</p></div>
                </div>
              )}

              {ut.review_comment && (
                <div className="error" style={{ marginBottom: '1.25rem' }}>
                  <strong>Комментарий проверяющего:</strong> {ut.review_comment}
                </div>
              )}

              {fileNames.length > 0 && (
                <div>
                  <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>
                    Прикреплённые файлы ({fileNames.length})
                  </h3>
                  <TaskFileList
                    filenames={fileNames}
                    folder="tasks"
                    onOpen={(i) => fileViewer.open(fileNames, i)}
                    onDownload={(fn) => fileAPI.downloadFile(fn)}
                  />
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {fileViewer.isOpen && (
        <FileViewer
          files={fileViewer.files} currentIndex={fileViewer.currentIndex}
          folder="tasks" onClose={fileViewer.close}
          onNext={fileViewer.next} onPrev={fileViewer.prev}
          onDownload={filename => fileAPI.downloadFile(filename)}
        />
      )}
    </div>
  );
};

export default Tasks;
